import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { analyticsLabels } from "@/components/global-nav/user-profile-new/src";
import WplButton from "@/components/wpl/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { AtlassianLogoArrowLinkBrand } from "@/libraries/wpl/foundations/src/logos/atlassian-logo-arrow-link-brand";

import Link from "../../../../components/wpl/link/src/index";
import { GlobalNavLinkContentProps } from "../nav-links-container";
import SearchButton from "../search-button";

import { Divider } from "./icons/divider";
import { DropdownType } from "./mobile-nav";
import { MobileNavLink } from "./mobile-nav-link";

const linkContainerCss = css({
    listStyle: "none",
    padding: "0 0 24px 0",
    margin: "0",
    fontWeight: "700",
    fontSize: "14px"
});

const signInContainerCss = css({
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "space-between",
    "alignItems": "center",
    "paddingBlockStart": "8px",
    "paddingBlockEnd": "8px",
    "#global-nav-search-icon": {
        margin: "0"
    },
    [media.above.sm]: {
        display: "none"
    }
});

const returnToHomeStyle = css({
    display: "none",
    [media.below.sm]: {
        display: "block"
    }
});

const getItFreeCss = css({
    display: "none",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: "8px",
        marginBlockEnd: "8px",
        height: "36px",
        a: {
            width: "100%",
            height: "100%",
            borderRadius: "20px 20px 20px 20px",
            fontWeight: "600"
        }
    }
});

const atlassianArrowLinkContainerStyle = css({
    height: "63.29px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const signInCss = css({
    paddingBlock: token("space.200")
});

export type MobileNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly dropdownType?: DropdownType;
};

export type MobileNavLinksContainerInfo = {
    key: string;
    mobileLinksProps: MobileNavLinkContentProps[];
};

interface MobileNavLinksContainerProps extends MobileNavLinksContainerInfo {
    handleMenuPage: (menuPage: DropdownType) => void;
    isIncludeBottomDivider?: boolean;
}

const MobileNavLinksContainer = ({
    handleMenuPage,
    key,
    mobileLinksProps,
    isIncludeBottomDivider = true
}: MobileNavLinksContainerProps) => {
    return (
        <>
            <ul className={key} css={linkContainerCss}>
                {mobileLinksProps.map(
                    (mobileLink: GlobalNavLinkContentProps) => {
                        return (
                            <li
                                className="nav-link"
                                key={mobileLink.globalLinksKey}
                            >
                                <MobileNavLink
                                    handleMenuPage={handleMenuPage}
                                    {...mobileLink}
                                />
                            </li>
                        );
                    }
                )}
            </ul>
            {isIncludeBottomDivider && <Divider />}
        </>
    );
};

type MobileNavLinksProps = {
    isLoggedIn: boolean;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    mobileLinksSections: MobileNavLinksContainerInfo[];
    productKey?: ProductKeyTypes;
    isIncludeCTA?: boolean;
    ctaText?: string;
    ctaUrl?: string;
    ctaOnClick?: (e: React.MouseEvent) => void;
    isIncludeAtlassianLogo?: boolean;
};

export const MobileNavLinks = ({
    isLoggedIn,
    handleMenuPage,
    mobileLinksSections,
    productKey = "jira",
    isIncludeCTA = false,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = false
}: MobileNavLinksProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    const homeHref = useLocalizedHref("/");
    const signinLink = {
        text: intl.formatMessage({
            id: "globalnavv2.mobile.navlink.signin",
            defaultMessage: "Sign in"
        }),
        href: signInUrl
    };

    const onLogInClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.login",
            label: analyticsLabels["JoinSiteUserDropdown.logIn"]
        }).fire();
    };

    return (
        <div>
            {isIncludeCTA && (
                <div css={getItFreeCss}>
                    <WplButton
                        text={ctaText}
                        url={ctaOnClick ? undefined : ctaUrl}
                        size="small"
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "navbar",
                            "data-label": ctaText,
                            "data-label-english": "Get it free",
                            "data-track-event": `${productKey}.get-it-free-button-clicked`
                        }}
                        onClick={ctaOnClick}
                    />
                </div>
            )}
            {mobileLinksSections.map(({ key, mobileLinksProps }) => (
                <MobileNavLinksContainer
                    key={key}
                    mobileLinksProps={mobileLinksProps}
                    handleMenuPage={handleMenuPage}
                    isIncludeBottomDivider={false}
                />
            ))}
            {isIncludeAtlassianLogo && (
                <div css={returnToHomeStyle}>
                    <div css={atlassianArrowLinkContainerStyle}>
                        <a
                            className="navigation-bar-atlas-icon"
                            href={homeHref}
                            aria-label={intl.formatMessage({
                                id: "productPageNav.atlassian-logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageMobileNav"
                            data-product-key="home"
                        >
                            <AtlassianLogoArrowLinkBrand
                                width={"311px"}
                                height={"15.29px"}
                            />
                        </a>
                    </div>
                    <Divider />
                </div>
            )}
            <div css={signInContainerCss}>
                <SearchButton
                    onClick={(e) => {
                        e.preventDefault();
                        handleMenuPage("search");
                    }}
                />
                {!isLoggedIn && (
                    <Link
                        key={signinLink.text}
                        text={signinLink.text}
                        href={signinLink.href}
                        type="basic"
                        css={signInCss}
                        doesArrowAnimate={true}
                        onClick={onLogInClick}
                        ariaLabel={intl.formatMessage({
                            id: "globalnavv2.mobile.navlink.signin.ariaLabel",
                            defaultMessage: "Sign in link"
                        })}
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event-component": "linkButton",
                            "data-event-container": "navbarDropdown",
                            "data-label": signinLink.text,
                            "data-event": "clicked"
                        }}
                    />
                )}
            </div>
        </div>
    );
};
